import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Info,
  Search,
  Settings2,
  ShieldCheck,
  Truck
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ROUTE from 'src/utils/routeConstants';
import { tyreManagementApi } from '../api/tyreManagementApi';
import useAsyncData from '../hooks/useAsyncData';
import { tyreQueryKeys } from '../api/queryKeys';
import useTyreMutation from '../hooks/useTyreMutation';
import ModuleHeader from '../components/ModuleHeader';
import EmptyState from '../components/EmptyState';
import StateBadge from '../components/StateBadge';
import SetupLayoutPicker from '../components/SetupLayoutPicker';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { SetupVehicleListSkeleton } from '../components/TmSkeletonLayouts';
import { suggestVehicleClassification } from '../components/vehicleModels/domain/classificationSuggestions';
import {
  commonClassKeyOf,
  hasMixedConfirmedClasses
} from '../components/vehicleModels/domain/setupLayoutPicker';

const TyreSetupPage = () => {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedLayout, setSelectedLayout] = useState('');
  const [reason, setReason] = useState('Initial vehicle-type mapping');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configuredVehicleIds, setConfiguredVehicleIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const rightColumnRef = useRef(null);
  const hydratedVehicleQueryRef = useRef('');
  const [vehiclePanelHeight, setVehiclePanelHeight] = useState(null);
  const configureVehicleMutation = useTyreMutation(tyreManagementApi.configureVehicle);
  const vehiclesState = useAsyncData(tyreQueryKeys.vehicles(), () => tyreManagementApi.getVehicles());
  const layoutsState = useAsyncData(tyreQueryKeys.layouts(), () => tyreManagementApi.getLayouts());
  const configState = useAsyncData(tyreQueryKeys.configuration(), tyreManagementApi.getConfiguration);
  const vehicles = vehiclesState.data || [];
  const layouts = layoutsState.data || [];
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query
      ? vehicles.filter((vehicle) => `${vehicle.registration_num} ${vehicle.model || ''} ${vehicle.site_name || ''}`
        .toLowerCase()
        .includes(query))
      : vehicles;
  }, [search, vehicles]);
  const selectedVehicleRecords = useMemo(
    () => vehicles.filter((vehicle) => selectedVehicles.includes(vehicle.id)),
    [selectedVehicles, vehicles]
  );
  const mixedConfirmedClasses = hasMixedConfirmedClasses(
    selectedVehicleRecords
  );

  useEffect(() => {
    const requestedVehicleId = new URLSearchParams(location.search).get(
      'vehicle'
    );
    if (!requestedVehicleId) {
      hydratedVehicleQueryRef.current = '';
      return;
    }
    if (!vehicles.length) return;
    const hydrationKey = `${requestedVehicleId}:${layouts.length}`;
    if (hydratedVehicleQueryRef.current === hydrationKey) return;
    const requestedVehicle = vehicles.find(
      (vehicle) => String(vehicle.id) === requestedVehicleId
    );
    if (requestedVehicle) {
      hydratedVehicleQueryRef.current = hydrationKey;
      setSelectedVehicles((current) => current.includes(requestedVehicle.id) ? current : [requestedVehicle.id]);
      const inferredClass = commonClassKeyOf([requestedVehicle], layouts);
      if (inferredClass) {
        setSelectedClass((current) => current || inferredClass);
      }
      setSelectedLayout('');
    }
  }, [layouts, location.search, vehicles]);

  useEffect(() => {
    const column = rightColumnRef.current;
    if (!column) return undefined;

    const mediaQuery = window.matchMedia('(min-width: 1280px)');
    let frameId = null;

    const syncPanelHeight = () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const nextHeight = mediaQuery.matches ? column.offsetHeight : null;
        setVehiclePanelHeight((currentHeight) => currentHeight === nextHeight ? currentHeight : nextHeight);
        frameId = null;
      });
    };

    syncPanelHeight();
    const observer = new ResizeObserver(syncPanelHeight);
    observer.observe(column);
    mediaQuery.addEventListener('change', syncPanelHeight);

    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      observer.disconnect();
      mediaQuery.removeEventListener('change', syncPanelHeight);
    };
  }, []);

  const toggleVehicle = (vehicleId) => {
    const nextSelection = selectedVehicles.includes(vehicleId)
      ? selectedVehicles.filter((id) => id !== vehicleId)
      : [...selectedVehicles, vehicleId];
    const nextRecords = vehicles.filter((vehicle) => nextSelection.includes(vehicle.id));
    setSelectedVehicles(nextSelection);
    setSelectedClass(commonClassKeyOf(nextRecords, layouts) || '');
    setSelectedLayout('');
    setError('');
    setSuccess('');
    setConfiguredVehicleIds([]);
  };

  const selectClass = (classKey) => {
    setSelectedClass(classKey);
    setSelectedLayout('');
    setError('');
    setSuccess('');
    setConfiguredVehicleIds([]);
  };

  const applyConfiguration = async () => {
    setError('');
    setSuccess('');
    if (mixedConfirmedClasses) {
      setError(
        'The selected vehicles have different confirmed classes. Configure each class separately.'
      );
      return;
    }
    if (!selectedVehicles.length || !selectedClass || !selectedLayout) {
      setError(
        'Select at least one vehicle, confirm its class, and choose an approved axle layout.'
      );
      return;
    }
    setSubmitting(true);
    try {
      const results = await Promise.allSettled(
        selectedVehicles.map((vehicleId) => configureVehicleMutation.mutateAsync({
          vehicle_id: vehicleId,
          layout_template_id: selectedLayout,
          reason
        }))
      );
      const failedVehicleIds = results.reduce((ids, result, index) => (
        result.status === 'rejected' ? [...ids, selectedVehicles[index]] : ids
      ), []);
      const successfulVehicleIds = selectedVehicles.filter(
        (vehicleId) => !failedVehicleIds.includes(vehicleId)
      );
      const failed = results.filter((result) => result.status === 'rejected');
      const completed = successfulVehicleIds.length;
      if (completed) {
        setSuccess(
          failed.length
            ? `${completed} vehicle${completed === 1 ? '' : 's'} configured. ${failed.length} still need setup.`
            : `${completed} vehicle${completed === 1 ? '' : 's'} configured. Continue to assign tyres.`
        );
      }
      setConfiguredVehicleIds(successfulVehicleIds);
      if (failed.length) {
        const failureMessage = failed[0].reason?.message || 'Please try the remaining vehicles again.';
        setError(
          `${failed.length} vehicle${failed.length === 1 ? '' : 's'} could not be configured. ${failureMessage}`
        );
      }
      setSelectedVehicles(failedVehicleIds);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <ModuleHeader
        eyebrow="Vehicle setup"
        title="Configure vehicle layouts"
        description="Select vehicles and assign their approved tyre layout."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {configState.data?.state && <StateBadge value={configState.data.state} />}
            <Button asChild variant="outline">
              <Link to={ROUTE.vehicles}>Vehicle List</Link>
            </Button>
            <Button asChild>
              <Link to={ROUTE.tyreV2Vehicles}>Tyre workspace</Link>
            </Button>
          </div>
        }
      />
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <p className="mt-3 font-extrabold text-slate-950">
              Approved layouts
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Layouts are published and maintained by Retrack.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <Truck className="h-6 w-6 text-blue-600" />
            <p className="mt-3 font-extrabold text-slate-950">
              Vehicle records
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Registration, site, and status come from Vehicle Management.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <Settings2 className="h-6 w-6 text-orange-600" />
            <p className="mt-3 font-extrabold text-slate-950">Safe changes</p>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              Remove fitted tyres before changing a layout.
            </p>
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-col gap-5 xl:flex-row xl:items-stretch">
        <div
          className="min-h-0 flex-1 overflow-hidden"
          style={
            vehiclePanelHeight != null
              ? { height: vehiclePanelHeight }
              : undefined
          }
        >
          <Card className="flex h-full flex-col overflow-hidden">
            <CardHeader className="shrink-0">
              <CardTitle>1. Select vehicles</CardTitle>
              <p className="text-sm text-slate-500">
                Bulk-select only vehicles that truly share the same wheel
                configuration.
              </p>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col">
              <div className="relative mb-4 shrink-0">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="pl-9"
                  placeholder="Search vehicle, model, or site"
                  disabled={vehiclesState.loading}
                />
              </div>
              <div
                className="tm-scrollbar min-h-0 flex-1 space-y-2 overflow-x-hidden overflow-y-auto"
              >
                {vehiclesState.loading && <SetupVehicleListSkeleton />}
                {!vehiclesState.loading && vehiclesState.error && (
                  <EmptyState
                    title="Vehicles unavailable"
                    description={vehiclesState.error}
                    action={<Button onClick={vehiclesState.reload}>Retry</Button>}
                  />
                )}
                {!vehiclesState.loading
                  && filtered.map((vehicle) => {
                    const suggested = suggestVehicleClassification(
                      vehicle,
                      layouts
                    );
                    const checked = selectedVehicles.includes(vehicle.id);
                    return (
                      <label
                        key={vehicle.id}
                        data-selected={checked || undefined}
                        className="tm-select-item-label"
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-primary"
                          checked={checked}
                          onChange={() => toggleVehicle(vehicle.id)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-extrabold text-slate-950">
                              {vehicle.registration_num}
                            </span>
                            <StateBadge value={vehicle.tyre_readiness} />
                          </span>
                          <span className="mt-1 block text-sm text-slate-600">
                            {vehicle.model
                              || vehicle.category
                              || 'Model not recorded'}{' '}
                            · {vehicle.site_name}
                          </span>
                          {suggested && !vehicle.active_configuration_id && (
                            <span className="mt-2 inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                              <Info className="h-3.5 w-3.5" />
                              <span>Suggested class: {suggested.label}</span>
                              <span className="font-medium text-blue-600">
                                ·{' '}
                                {suggested.status === 'needs_layout'
                                  ? 'Layout review required'
                                  : 'Review before applying'}
                              </span>
                              {suggested.matchedAlias && (
                                <span className="w-full pl-5 font-normal text-blue-600">
                                  Matched “{suggested.matchedAlias}” ·{' '}
                                  {suggested.confidence} confidence
                                </span>
                              )}
                            </span>
                          )}
                          {vehicle.layout_name && (
                            <span className="mt-2 block text-xs font-semibold text-primary">
                              Current layout: {vehicle.layout_name}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                {!vehiclesState.loading
                  && !vehiclesState.error
                  && Boolean(vehicles.length)
                  && !filtered.length && (
                    <EmptyState
                      title="No vehicles found"
                      description="No opted-in vehicles match the current search."
                    />
                )}
                {!vehiclesState.loading
                  && !vehiclesState.error
                  && !vehicles.length && (
                    <EmptyState
                      title="No vehicles have tyre tracking on"
                      description="Turn on tyre tracking from the Vehicle List before assigning layouts."
                      action={
                        <Button asChild>
                          <Link to={ROUTE.vehicles}>Open Vehicle List</Link>
                        </Button>
                      }
                    />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        <div ref={rightColumnRef} className="w-full max-w-[440px] shrink-0">
          <SetupLayoutPicker
            layouts={layouts}
            loading={layoutsState.loading}
            loadError={layoutsState.error}
            selectedClass={selectedClass}
            selectedLayout={selectedLayout}
            selectedVehicleCount={selectedVehicles.length}
            mixedConfirmedClasses={mixedConfirmedClasses}
            reason={reason}
            error={error}
            success={success}
            successAction={configuredVehicleIds.length ? (
              <Button asChild variant="outline" className="w-full">
                <Link to={configuredVehicleIds.length === 1
                  ? `${ROUTE.tyreV2Vehicles}?vehicle=${encodeURIComponent(configuredVehicleIds[0])}`
                  : ROUTE.tyreV2Vehicles}
                >
                  Assign tyres <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Button>
            ) : null}
            submitting={submitting}
            onClassSelect={selectClass}
            onLayoutSelect={setSelectedLayout}
            onReasonChange={(event) => setReason(event.target.value)}
            onApply={applyConfiguration}
            onRetryLoad={layoutsState.reload}
          />
        </div>
      </div>
    </div>
  );
};

export default TyreSetupPage;
